import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getPlayer } from '@/lib/api'
import { PageMotion, Stagger, StaggerItem } from '@/components/Motion'
import {
  EmptyState,
  ErrorBlock,
  LoadingBlock,
  PageHeader,
  Panel,
  StatStrip,
} from '@/components/ui'
import { formatKD, formatNum, formatPct, sideColor } from '@/lib/utils'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

const chartTooltip = {
  background: '#222222',
  border: '1px solid #2e2e2e',
  borderRadius: 6,
  fontSize: 12,
  color: '#e5e5e5',
}

export default function PlayerDetail() {
  const { id = '' } = useParams()
  const { data, isLoading, error } = useQuery({
    queryKey: ['player', id],
    queryFn: () => getPlayer(id),
    enabled: !!id,
  })

  if (isLoading) return <LoadingBlock />
  if (error || !data) return <ErrorBlock message="Player not found" />

  const weapons = (data.favoriteWeapons ?? []).slice(0, 8).map((w) => ({
    name: w.weapon.length > 16 ? w.weapon.slice(0, 14) + '…' : w.weapon,
    count: w.count,
  }))

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        breadcrumb={
          <p className="text-xs uppercase tracking-[0.14em] text-ink-faint">
            <Link to="/players" className="hover:text-accent-text transition-colors">
              Players
            </Link>
            <span className="mx-2">/</span>#{data.id}
          </p>
        }
        title={data.name}
      />

      <StatStrip
        items={[
          { label: 'Kills', value: data.totalKills },
          { label: 'Deaths', value: data.totalDeaths },
          { label: 'K/D', value: formatKD(data.kd) },
          { label: 'Accuracy', value: formatPct(data.accuracy) },
        ]}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Missions', value: formatNum(data.missionsPlayed) },
          { label: 'Shots', value: formatNum(data.totalShots) },
          { label: 'Hits', value: formatNum(data.totalHits) },
          {
            label: 'Avg distance',
            value: `${formatNum(data.avgDistance, 0)}m`,
            hint: `${formatNum(data.minDistance, 0)}–${formatNum(data.maxDistance, 0)}m`,
          },
        ].map((item) => (
          <div key={item.label} className="panel px-4 py-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-ink-faint">
              {item.label}
            </div>
            <div className="stat-value mt-1.5 text-xl">{item.value}</div>
            {'hint' in item && item.hint ? (
              <div className="mt-0.5 text-xs text-ink-faint">{item.hint}</div>
            ) : null}
          </div>
        ))}
      </div>

      <Stagger className="grid gap-4 lg:grid-cols-2">
        <StaggerItem>
          <Panel title="Favorite weapons">
            {weapons.length === 0 ? (
              <EmptyState message="No kills recorded" />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weapons} layout="vertical" margin={{ left: 4, right: 8 }}>
                    <XAxis type="number" hide />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={110}
                      tick={{ fill: '#a3a3a3', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={chartTooltip} />
                    <Bar dataKey="count" fill="#8b9a7c" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Sides played">
            {(data.sideStats ?? []).length === 0 ? (
              <EmptyState message="No side data" />
            ) : (
              <ul className="space-y-2.5">
                {data.sideStats.map((s) => (
                  <li key={s.side} className="flex justify-between text-sm">
                    <span className={sideColor(s.side)}>{s.side || 'UNKNOWN'}</span>
                    <span className="font-mono text-ink-muted">{formatNum(s.count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Top victims">
            {(data.topVictims ?? []).length === 0 ? (
              <EmptyState message="No victims" />
            ) : (
              <ol className="space-y-0.5">
                {data.topVictims.map((v, i) => (
                  <li key={v.playerId}>
                    <Link
                      to={`/players/${v.playerId}`}
                      className="group flex justify-between gap-3 rounded-control px-3 py-2.5 transition-colors hover:bg-accent-soft"
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-ink-faint w-5 tabular-nums">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="truncate text-ink group-hover:text-accent-text transition-colors">
                          {v.victimName}
                        </span>
                      </span>
                      <span className="font-mono text-accent-text tabular-nums">{v.count}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </StaggerItem>

        <StaggerItem>
          <Panel title="Top killers">
            {(data.topKillers ?? []).length === 0 ? (
              <EmptyState message="No deaths" />
            ) : (
              <ol className="space-y-0.5">
                {data.topKillers.map((k, i) => (
                  <li key={k.playerId}>
                    <Link
                      to={`/players/${k.playerId}`}
                      className="group flex justify-between gap-3 rounded-control px-3 py-2.5 transition-colors hover:bg-accent-soft"
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-ink-faint w-5 tabular-nums">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="truncate text-ink group-hover:text-accent-text transition-colors">
                          {k.killerName}
                        </span>
                      </span>
                      <span className="font-mono text-rose-400 tabular-nums">{k.count}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </StaggerItem>
      </Stagger>

      <Panel title="Mission history" flush>
        {(data.missionHistory ?? []).length === 0 ? (
          <div className="p-4">
            <EmptyState message="No missions" />
          </div>
        ) : (
          <div className="table-wrap border-0 rounded-none">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Mission</th>
                  <th>Map</th>
                  <th>Side</th>
                  <th>K</th>
                  <th>D</th>
                  <th>Acc</th>
                </tr>
              </thead>
              <tbody>
                {data.missionHistory.map((m) => (
                  <tr key={m.missionId}>
                    <td>
                      <Link to={`/missions/${m.missionId}`} className="link-accent font-medium">
                        {m.missionName || `#${m.missionId}`}
                      </Link>
                    </td>
                    <td>{m.worldname}</td>
                    <td className={sideColor(m.side)}>{m.side || '—'}</td>
                    <td className="font-mono text-accent-text">{m.kills}</td>
                    <td className="font-mono">{m.deaths}</td>
                    <td className="font-mono text-xs">
                      {m.shots ? formatPct((m.hits / m.shots) * 100) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </PageMotion>
  )
}
